import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/lib/toast";
import { Plus, Upload } from "lucide-react";
import { useRealtimeClients } from "@/hooks/use-realtime-clients";

interface CreateApprovalDialogProps {
  onApprovalCreated: () => void;
  trigger?: React.ReactNode;
}

export function CreateApprovalDialog({ onApprovalCreated, trigger }: CreateApprovalDialogProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { clients, isLoading: clientsLoading } = useRealtimeClients();
  const [formData, setFormData] = useState({
    title: "",
    client_id: "",
    caption: "",
    platform: "instagram",
    post_type: "static",
    instagram_handle: "",
    post_date: ""
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [carouselFiles, setCarouselFiles] = useState<File[]>([]);

  const resetForm = () => {
    setFormData({
      title: "",
      client_id: "",
      caption: "",
      platform: "instagram",
      post_type: "static",
      instagram_handle: "",
      post_date: ""
    });
    setImageFile(null);
    setVideoFile(null);
    setCarouselFiles([]);
  };

  const uploadFile = async (file: File, folder: string = 'approval-media') => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
      const filePath = `${folder}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('approval_images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('approval_images')
        .getPublicUrl(filePath);

      return data.publicUrl;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.client_id) {
      toast.error("Título e cliente são obrigatórios");
      return;
    }

    if (!formData.post_date) {
      toast.error("Data de postagem é obrigatória");
      return;
    }

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuário não autenticado");

      // Buscar company_id do usuário
      let targetCompanyId: string | null = null;

      // Verificar se é colaborador
      const { data: collaboratorData } = await supabase
        .from('collaborators')
        .select('company_id')
        .eq('collaborator_id', user.id)
        .eq('status', 'active')
        .maybeSingle();

      if (collaboratorData) {
        targetCompanyId = collaboratorData.company_id;
      } else {
        const { data: userData } = await supabase
          .from('users')
          .select('company_id')
          .eq('id', user.id)
          .single();
        targetCompanyId = userData?.company_id || null;
      }

      if (!targetCompanyId) {
        toast.error("Empresa não encontrada");
        return;
      }

      // Buscar todos os colaboradores ativos da empresa para adicionar na visibilidade
      const { data: collaborators } = await supabase
        .from('collaborators')
        .select('collaborator_id')
        .eq('company_id', targetCompanyId)
        .eq('status', 'active');

      // Buscar proprietários da empresa
      const { data: companyData } = await supabase
        .from('companies')
        .select('owner_ids')
        .eq('id', targetCompanyId)
        .single();

      // Criar lista de usuários que podem ver a aprovação
      const visibleTo = [user.id]; // Sempre incluir o criador
      
      // Adicionar colaboradores ativos
      if (collaborators) {
        collaborators.forEach(collab => {
          if (!visibleTo.includes(collab.collaborator_id)) {
            visibleTo.push(collab.collaborator_id);
          }
        });
      }
      
      // Adicionar proprietários da empresa
      if (companyData?.owner_ids && Array.isArray(companyData.owner_ids)) {
        companyData.owner_ids.forEach((ownerId: string) => {
          if (!visibleTo.includes(ownerId)) {
            visibleTo.push(ownerId);
          }
        });
      }

      // Buscar nome do cliente selecionado
      const selectedClient = clients.find(c => c.id === formData.client_id);
      if (!selectedClient) {
        toast.error("Cliente não encontrado");
        return;
      }

      let imageUrl = null;
      let carouselImages: string[] = [];
      let reelsVideo = null;

      // Upload de arquivos baseado no tipo de post
      if (formData.post_type === 'static' && imageFile) {
        imageUrl = await uploadFile(imageFile);
      } else if (formData.post_type === 'carousel' && carouselFiles.length > 0) {
        const uploadPromises = carouselFiles.map(file => uploadFile(file));
        carouselImages = await Promise.all(uploadPromises);
        imageUrl = carouselImages[0]; // Primeira imagem como capa
      } else if (formData.post_type === 'reels' && videoFile) {
        reelsVideo = await uploadFile(videoFile);
      }

      // Gerar slug único
      const slug = `${formData.title.toLowerCase().replace(/[^a-z0-9]/g, '-')}-${Date.now()}`;
      const baseDomain = window.location.origin;
      const shareableLink = `${baseDomain}/approve/${slug}`;

      // Convert post_date to ISO string for database storage
      const postDate = new Date(formData.post_date).toISOString();

      const approvalData = {
        title: formData.title,
        client_name: selectedClient.name,
        caption: formData.caption,
        platform: formData.platform,
        post_type: formData.post_type,
        instagram_handle: formData.instagram_handle || null,
        image_url: imageUrl,
        carousel_images: carouselImages.length > 0 ? carouselImages : null,
        reels_video: reelsVideo,
        slug: slug,
        shareable_link: shareableLink,
        user_id: user.id,
        company_id: targetCompanyId,
        status: 'pending',
        post_date: postDate,
        visible_to: visibleTo
      };

      const { error } = await supabase
        .from('approval_posts')
        .insert([approvalData]);

      if (error) throw error;

      toast.success("Post criado com sucesso!");
      setOpen(false);
      resetForm();
      onApprovalCreated();
    } catch (error: any) {
      console.error('Error creating approval:', error);
      toast.error(`Erro ao criar post: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'image' | 'video' | 'carousel') => {
    const files = e.target.files;
    if (!files) return;

    if (type === 'image') {
      setImageFile(files[0]);
    } else if (type === 'video') {
      setVideoFile(files[0]);
    } else if (type === 'carousel') {
      setCarouselFiles(Array.from(files));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button>
            <Plus className="w-4 h-4 mr-2" />
            Novo Post
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Criar Novo Post para Aprovação</DialogTitle>
          <DialogDescription>
            Preencha os detalhes do post que será enviado para aprovação.
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="title" className="text-right">
              Título *
            </Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="col-span-3"
            />
          </div>

          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="client_id" className="text-right">
              Cliente *
            </Label>
            <Select value={formData.client_id} onValueChange={(value) => setFormData(prev => ({ ...prev, client_id: value }))}>
              <SelectTrigger className="col-span-3">
                <SelectValue placeholder="Selecionar cliente" />
              </SelectTrigger>
              <SelectContent>
                {clientsLoading ? (
                  <SelectItem value="" disabled>Carregando clientes...</SelectItem>
                ) : clients.length === 0 ? (
                  <SelectItem value="" disabled>Nenhum cliente encontrado</SelectItem>
                ) : (
                  clients.map(client => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="post_date" className="text-right">
              Data de Postagem *
            </Label>
            <Input
              id="post_date"
              type="date"
              value={formData.post_date}
              onChange={(e) => setFormData(prev => ({ ...prev, post_date: e.target.value }))}
              className="col-span-3"
            />
          </div>

          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="platform" className="text-right">
              Plataforma
            </Label>
            <Select value={formData.platform} onValueChange={(value) => setFormData(prev => ({ ...prev, platform: value }))}>
              <SelectTrigger className="col-span-3">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="instagram">Instagram</SelectItem>
                <SelectItem value="facebook">Facebook</SelectItem>
                <SelectItem value="linkedin">LinkedIn</SelectItem>
                <SelectItem value="twitter">Twitter</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="post_type" className="text-right">
              Tipo de Post
            </Label>
            <Select value={formData.post_type} onValueChange={(value) => setFormData(prev => ({ ...prev, post_type: value }))}>
              <SelectTrigger className="col-span-3">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="static">Post Estático</SelectItem>
                <SelectItem value="carousel">Carrossel</SelectItem>
                <SelectItem value="reels">Reels/Vídeo</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formData.platform === 'instagram' && (
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="instagram_handle" className="text-right">
                @Handle do Instagram
              </Label>
              <Input
                id="instagram_handle"
                value={formData.instagram_handle}
                onChange={(e) => setFormData(prev => ({ ...prev, instagram_handle: e.target.value }))}
                placeholder="@seuperfil"
                className="col-span-3"
              />
            </div>
          )}

          <div className="grid grid-cols-4 items-start gap-4">
            <Label htmlFor="caption" className="text-right">
              Legenda
            </Label>
            <Textarea
              id="caption"
              value={formData.caption}
              onChange={(e) => setFormData(prev => ({ ...prev, caption: e.target.value }))}
              className="col-span-3"
              rows={4}
            />
          </div>

          {/* Upload de arquivos baseado no tipo */}
          {formData.post_type === 'static' && (
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="image" className="text-right">
                Imagem
              </Label>
              <div className="col-span-3">
                <Input
                  id="image"
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, 'image')}
                />
              </div>
            </div>
          )}

          {formData.post_type === 'carousel' && (
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="carousel" className="text-right">
                Imagens (múltiplas)
              </Label>
              <div className="col-span-3">
                <Input
                  id="carousel"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => handleFileChange(e, 'carousel')}
                />
                <p className="text-sm text-muted-foreground mt-1">
                  Selecione múltiplas imagens para o carrossel
                </p>
              </div>
            </div>
          )}

          {formData.post_type === 'reels' && (
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="video" className="text-right">
                Vídeo
              </Label>
              <div className="col-span-3">
                <Input
                  id="video"
                  type="file"
                  accept="video/*"
                  onChange={(e) => handleFileChange(e, 'video')}
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? "Criando..." : "Criar Post"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
